// Focus history — the window's ONE global back/forward stack over focused
// VIEWS, pure. (navigation-history-and-tab-lifecycle.md §2; synthesis D1/D6/D13.)
//
// Back walks WHERE YOU LOOKED (which view was focused), not a location inside a
// tab: our tab IS a view, so the unit of "back" is which view you focused, and
// there is exactly one focused view per window at a time — hence ONE global
// stack (D6), not a per-tab URL stack. This module is that stack as pure
// types + total folds; the shell only captures the keydown / mouse / swipe /
// popstate that dispatch `nav-back` / `nav-forward` and calls `recordFocus`
// on the navigating transitions.
//
// Kept DISTINCT from the other two recalls (§2d): focus-history (this module,
// the temporal focus SEQUENCE, cursor-walked) vs reopen-closed (`ClosedTabs` in
// types.ts — LIFO of DELIBERATE closes) vs recents (frecency). A replaced
// preview lives in focus-history (reachable by `nav-back`) but NEVER on the
// reopen stack — that cleave is what keeps the three non-redundant.
//
// Discipline (dev-ts): pure — no I/O, no Date/random, no id minting (a
// `HistoryView` reuses an already-branded `ResourceRef` — no construction, no
// `as`); named union on a `type` discriminant; folds total, `readonly`
// throughout, SAME reference on a no-op. Session-only: never persisted, never
// event-sourced — thrown away on reload by design.
//
// WORKSPACE-FLAT (flattened-object-model-design.md): the window holds exactly
// ONE working set now — there is no per-project tree to disambiguate, so a
// `HistoryView` no longer carries a project id. The former `workspace` arm
// (the cross-project landing, reached ABOVE the projects) is gone too: with no
// second "world" to park into, the workspace landing is one place —
// `HistoryLanding` (recorded whenever a hero tab is focused).

import {
    openInFocused,
    openPreviewInFocused,
    type PaneTree,
} from "./paneTree.js";
import type { ConversationId, ResourceRef } from "./types.js";

// === HistoryView — the re-openable record of one focused view ================
// A sum on `type`, mirroring ActiveTab — but the resource arm carries the REF
// (re-materializable after the view left the working set) and the REGISTER (so
// re-opening a glance re-previews, a kept re-keeps). `register` is a recorded
// FACT about how the view was reached, read only when re-materializing — NOT a
// meaning-swapping flag on a command (distinct-commands lives at the command
// layer).
//
// Hero tabs all record as the ONE `landing` view (id-blind): nav-back returns
// you to the landing, never to a specific blank hero — the same reason VS Code
// collapses its Welcome tab rather than tracking each one.
export type HistoryRegister = "preview" | "kept";
// The landing view — a hero tab is focused. Id-blind (all heroes are the one
// "landing" place), so nav-back returns you to the landing, not a specific blank
// hero. Replaces the former `HistoryHome` now the pinned home is gone.
export type HistoryLanding = { readonly type: "landing" };
export type HistoryResource = {
    readonly type: "resource";
    readonly ref: ResourceRef;
    readonly register: HistoryRegister;
};
export type HistoryConversation = {
    readonly type: "conversation";
    readonly id: ConversationId;
};
export type HistorySettings = { readonly type: "settings" };
// The Home overview view — the Settings-sibling singleton (dogfood F1).
export type HistoryHome = { readonly type: "home" };
// The Studio canvas view — the Home-sibling singleton (Studio v0).
export type HistoryStudio = { readonly type: "studio" };
// The Projects list view — the Home-sibling singleton (#161).
export type HistoryProjects = { readonly type: "projects" };
// The Knowledge bank view — the Home-sibling singleton (#60).
export type HistoryKnowledge = { readonly type: "knowledge" };
// The Search + Recent view — the Home-sibling singleton (dogfood F6).
export type HistorySearch = { readonly type: "search" };
export type HistoryView =
    | HistoryLanding
    | HistoryResource
    | HistoryConversation
    | HistorySettings
    | HistoryHome
    | HistoryStudio
    | HistoryProjects
    | HistoryKnowledge
    | HistorySearch;

// Constructors (the shell records with these; no id minting — the ref is passed
// in already branded).
export const landingView: HistoryLanding = { type: "landing" };
export const conversationView = (id: ConversationId): HistoryConversation => ({
    type: "conversation",
    id,
});
export const settingsView: HistorySettings = { type: "settings" };
export const homeView: HistoryHome = { type: "home" };
export const studioView: HistoryStudio = { type: "studio" };
export const projectsView: HistoryProjects = { type: "projects" };
export const knowledgeView: HistoryKnowledge = { type: "knowledge" };
export const searchView: HistorySearch = { type: "search" };
export const previewView = (ref: ResourceRef): HistoryResource => ({
    type: "resource",
    ref,
    register: "preview",
});
export const keptView = (ref: ResourceRef): HistoryResource => ({
    type: "resource",
    ref,
    register: "kept",
});

// Two views are the "same place" when they name the same view — register-blind
// for resources (a glance and a keep of the SAME resource are one place; the
// register upgrade is `promoteCurrent`, not a second entry). Drives the
// self-navigation no-op in `recordFocus`.
export const sameView = (a: HistoryView, b: HistoryView): boolean => {
    if (a.type !== b.type) return false;
    if (a.type === "resource" && b.type === "resource")
        return a.ref.id === b.ref.id;
    if (a.type === "conversation" && b.type === "conversation")
        return a.id === b.id;
    return true;
};

// === FocusHistory — the browser two-stack cursor, pure =======================
// `back` is oldest→newest (top = the view before current); `forward` holds the
// views reversed PAST (nearest first). Exactly one `current` always exists.
export type FocusHistory = {
    readonly back: readonly HistoryView[];
    readonly current: HistoryView;
    readonly forward: readonly HistoryView[];
};

// The window boots focused on home.
export const initialFocusHistory: FocusHistory = {
    back: [],
    current: landingView,
    forward: [],
};

// Bound the back leg — a working-memory aid, not an unbounded log (the author
// who wants further back has the rail, like CLOSED_TABS_CAP bounds the reopen
// stack). Oldest entries fall off the bottom.
const HISTORY_CAP = 50;
const capBack = (back: readonly HistoryView[]): readonly HistoryView[] =>
    back.length > HISTORY_CAP ? back.slice(back.length - HISTORY_CAP) : back;

// A NEW navigation: push current onto back, adopt the new view, and TRUNCATE
// forward (the canonical browser rule — this is what makes `forward` survivable
// and is fired on every navigating transition EXCEPT the two `nav-back`/
// `nav-forward` themselves cause). Re-focusing the view you are already on is a
// same-reference no-op (no self-entry).
export const recordFocus = (
    h: FocusHistory,
    view: HistoryView,
): FocusHistory =>
    sameView(h.current, view)
        ? h
        : { back: capBack([...h.back, h.current]), current: view, forward: [] };

// Promotion (double-click / edit) keeps you on the SAME view — it changes the
// view's register, not your position (§3d.3). Flip current preview→kept in
// place so a later `nav-back` that re-materializes it re-opens it KEPT; leave
// back/forward untouched. No-op for any non-preview current.
export const promoteCurrent = (h: FocusHistory): FocusHistory =>
    h.current.type === "resource" && h.current.register === "preview"
        ? { ...h, current: { ...h.current, register: "kept" } }
        : h;

// nav-back / nav-forward: move the cursor across; do NOT recordFocus (else you
// could never leave). `undefined` = nothing that way → the shell no-ops (a
// disabled affordance, D13 — back past the first entry never invents a
// destination).
export const goBack = (h: FocusHistory): FocusHistory | undefined => {
    const prev = h.back.at(-1);
    if (prev === undefined) return undefined;
    return {
        back: h.back.slice(0, -1),
        current: prev,
        forward: [h.current, ...h.forward],
    };
};
export const goForward = (h: FocusHistory): FocusHistory | undefined => {
    const [next, ...rest] = h.forward;
    if (next === undefined) return undefined;
    return { back: [...h.back, h.current], current: next, forward: rest };
};

// Availability predicates — gate the omnibar Back/Forward rows + the ribbon
// affordances so they dim when the respective leg is empty.
export const canGoBack = (h: FocusHistory): boolean => h.back.length > 0;
export const canGoForward = (h: FocusHistory): boolean => h.forward.length > 0;

// === Re-materialize (D1) — apply a recalled view back into the pane tree =====
// The pure half of `nav-back`/`nav-forward`'s effect: a RESOURCE view is
// re-materialized into the focused pane — as a PREVIEW when it was reached as a
// glance, as a KEPT tab when it was kept — resurrecting it even after it left
// the working set (a replaced preview, a closed tab). This is the vscode#166194
// fix: no glance is a dead end. Non-resource views (home / conversation /
// settings) route the CENTRE's destination in the shell, not the pane tree, so
// the tree is returned unchanged for them.
export const materializeView = (t: PaneTree, view: HistoryView): PaneTree => {
    if (view.type !== "resource") return t;
    return view.register === "preview"
        ? openPreviewInFocused(t, view.ref)
        : openInFocused(t, {
              type: "resource",
              resource: view.ref,
              dirty: false,
          });
};
